import { Avatar } from "@heroui/react";
import { Carousel } from "@heroui-pro/react/carousel";
import { Rating } from "@heroui-pro/react/rating";
import type { EmblaCarouselType } from "embla-carousel";
import { memo, useEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";

export type Testimonial = {
  id: string;
  rating: 1 | 2 | 3 | 4 | 5;
  body: string;
  authorName: string;
  authorAvatarUrl?: string;
};

type TestimonialCarouselProps = {
  items: Testimonial[];
  intervalMs?: number;
  autoplay?: boolean;
  showPagination?: boolean;
  className?: string;
  testId?: string;
};

const RESUME_DELAY_MS = 6000;

type SlideProps = { item: Testimonial; index: number; total: number };

const TestimonialSlide = ({ item, index, total }: SlideProps) => (
  <Carousel.Item role="group" aria-roledescription="slide" aria-label={`${index + 1} of ${total}`}>
    <div
      className="flex flex-col gap-3 rounded-[var(--brand-radius)] p-4"
      style={{ background: "hsl(var(--brand-bg-foreground) / 0.04)" }}
    >
      <Rating
        aria-label={`Rated ${item.rating} out of 5 stars`}
        value={item.rating}
        isReadOnly
        size="sm"
      >
        <Rating.Item value={1} />
        <Rating.Item value={2} />
        <Rating.Item value={3} />
        <Rating.Item value={4} />
        <Rating.Item value={5} />
      </Rating>
      <p className="text-sm leading-relaxed" style={{ color: "hsl(var(--brand-bg-foreground))" }}>
        {item.body}
      </p>
      <div className="mt-1 flex items-center gap-2">
        <Avatar size="sm">
          {item.authorAvatarUrl && <Avatar.Image src={item.authorAvatarUrl} alt="" />}
          <Avatar.Fallback>{item.authorName.slice(0, 1)}</Avatar.Fallback>
        </Avatar>
        <span className="text-xs" style={{ color: "hsl(var(--brand-bg-foreground) / 0.65)" }}>
          {item.authorName}
        </span>
      </div>
    </div>
  </Carousel.Item>
);

const TestimonialCarouselImpl = ({
  items,
  intervalMs = 4500,
  autoplay = true,
  showPagination = true,
  className,
  testId,
}: TestimonialCarouselProps) => {
  const prefersReduced = useReducedMotion();
  const [api, setApi] = useState<EmblaCarouselType>();
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const resumeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const autoplayEnabled = autoplay && !prefersReduced && items.length > 1 && !paused;

  useEffect(() => {
    if (!api) return;
    const onSelect = (): void => setActiveIndex(api.selectedScrollSnap());
    onSelect();
    api.on("select", onSelect);
    api.on("reInit", onSelect);
    return () => {
      api.off("select", onSelect);
      api.off("reInit", onSelect);
    };
  }, [api]);

  useEffect(() => {
    if (!api || !autoplayEnabled) return;
    const interval = setInterval(() => {
      api.scrollNext();
    }, intervalMs);
    return () => clearInterval(interval);
  }, [api, autoplayEnabled, intervalMs]);

  useEffect(() => {
    if (!api) return;
    const onPointerDown = (): void => {
      setPaused(true);
      if (resumeTimer.current) clearTimeout(resumeTimer.current);
      resumeTimer.current = setTimeout(() => setPaused(false), RESUME_DELAY_MS);
    };
    api.on("pointerDown", onPointerDown);
    return () => {
      api.off("pointerDown", onPointerDown);
      if (resumeTimer.current) clearTimeout(resumeTimer.current);
    };
  }, [api]);

  if (items.length === 0) return null;

  const activeBody = items[activeIndex]?.body ?? "";

  return (
    <div
      data-testid={testId}
      className={`w-full ${className ?? ""}`}
      role="region"
      aria-roledescription="carousel"
      aria-label="Member testimonials"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <Carousel opts={{ loop: items.length > 1 }} setApi={setApi}>
        <Carousel.Content>
          {items.map((item, index) => (
            <TestimonialSlide key={item.id} item={item} index={index} total={items.length} />
          ))}
        </Carousel.Content>
        {showPagination && items.length > 1 && <Carousel.Dots />}
      </Carousel>
      <span aria-live="polite" className="sr-only">
        {activeBody}
      </span>
    </div>
  );
};

export const TestimonialCarousel = memo(TestimonialCarouselImpl);

export default TestimonialCarousel;
